import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Handshake, MessageSquare, DollarSign, Clock, CheckCircle, XCircle, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { roomsAPI } from '../lib/api';
import { formatCurrency, formatRelativeTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const STATUS_MAP = {
  active: { label: 'Negotiating', color: 'bg-[#b8ff00]/20 text-[#b8ff00] border-[#b8ff00]/30', icon: MessageSquare },
  deal_made: { label: 'Deal Made', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Clock },
};

export default function DealRoomsListPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    roomsAPI.list().then(res => { setRooms(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#b8ff00] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-2xl flex items-center justify-center">
              <Handshake className="text-white" size={28} />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-zinc-900" data-testid="rooms-title">Deal Rooms</h1>
              <p className="text-zinc-500 text-sm">Private negotiation rooms with E2E encryption</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 rounded-xl border border-green-200">
          <Shield size={16} className="text-green-600 flex-shrink-0" />
          <p className="text-xs text-green-700">All conversations are end-to-end encrypted. Not even MMarketing can read your messages.</p>
        </div>

        {rooms.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200">
            <Handshake className="mx-auto mb-4 text-zinc-300" size={48} />
            <h3 className="font-display text-xl text-zinc-900 mb-2">No Deal Rooms Yet</h3>
            <p className="text-zinc-500 text-sm mb-4">Start a negotiation from any product or service page</p>
            <Link to="/marketplace"><Button className="bg-zinc-900 text-white rounded-full">Browse Marketplace</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {rooms.map((room, idx) => {
              const config = STATUS_MAP[room.status] || STATUS_MAP.active;
              const Icon = config.icon;
              const otherParty = user?.id === room.buyer_id ? room.seller_name : room.buyer_name;
              const myRole = user?.id === room.buyer_id ? 'Buyer' : 'Seller';

              return (
                <motion.div key={room.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                  <Link to={`/room/${room.id}`} className="block">
                    <div className="bg-white border border-zinc-200 rounded-2xl p-4 hover:border-zinc-300 transition-colors" data-testid={`room-${room.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-zinc-900 truncate">{room.item_name}</h3>
                          <p className="text-xs text-zinc-500 mt-0.5">with <strong>{otherParty}</strong> · You: {myRole}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full border font-medium flex items-center gap-1 flex-shrink-0 ${config.color}`}>
                          <Icon size={10} /> {config.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                        <span>Listed: {formatCurrency(room.listing_price)}</span>
                        {room.current_offer && <span className="text-amber-600 font-medium">Offer: {formatCurrency(room.current_offer)}</span>}
                        {room.deal_price && <span className="text-green-600 font-bold">Deal: {formatCurrency(room.deal_price)}</span>}
                        <span className="ml-auto">{formatRelativeTime(room.updated_at || room.created_at)}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
